import { MenuItem } from '@thyme/nashville/src/types/navbar'
import { ComputedRef } from 'vue'

/**
 * Menuitem.visible can be a boolean, undefined, or a function returning a boolean.
 * This evaluates it to a boolean regardless of which it is.
 * @param item
 * @returns boolean
 */
const evaluateVisibleKey = (item: MenuItem): boolean => {
  if (typeof item.visible === 'function') return item.visible()
  if (item.visible !== undefined) return item.visible
  return true
}

/**
 *  Add default, minimum visibility checks to menu items:
 *
 * // Menu item with URL → only visible if authorized
 * { label: 'Admin', url: '/admin' }
 *
 * // Menu item with children → visible if any child is visible
 * { label: 'Reports', items: [...] }
 * @param base
 * @param checkIfRouteAuthorized
 * @returns boolean
 */
const defaultVisibilityCheck = (
  base: MenuItem,
  checkIfRouteAuthorized: (path: string) => ComputedRef<boolean>
): boolean => {
  if (base.url) {
    return checkIfRouteAuthorized(base.url).value
  }
  if (base.items) {
    return base.items.some(evaluateVisibleKey)
  }
  return true
}

/**
 * Applies default visibility checks to menu items based on authorization and custom visibility rules.
 *
 * This function recursively processes menu items to enforce two key visibility behaviors:
 * 1. Menu items with URLs are only visible if the user is authorized to access that route
 * 2. Menu items with child items are visible if at least one child is visible
 * 3. Custom visibility rules (boolean or function) are combined with default checks using AND logic. This
 * allows ADDITIONAL restrictions to be applied on top of the defaults, such as only showing a MenuItem for
 * some users, even if all users have authorization to see a route.
 * @param menuItems - The array of menu items to process
 * @param checkIfRouteAuthorized - A function that takes a route path and returns a computed ref indicating authorization status
 * @returns A new array of menu items with visibility functions that evaluate both default and custom visibility rules
 * @example
 * ```typescript
 * const items = [
 *   { label: 'Admin', url: '/admin' },
 *   { label: 'Reports', items: [...] }
 * ]
 * const enhanced = applyVisibilityDefaults(items, checkIfRouteAuthorized)
 * ```
 */
export const applyVisibilityDefaults = (
  menuItems: MenuItem[],
  checkIfRouteAuthorized: (path: string) => ComputedRef<boolean>
): MenuItem[] => {
  const applyDefaults = (items: MenuItem[]): MenuItem[] => {
    return items.map((item) => {
      const base: MenuItem = {
        ...item,
        items: item.items ? applyDefaults(item.items) : undefined,
      }
      return {
        ...base,
        visible: () => {
          return (
            defaultVisibilityCheck(base, checkIfRouteAuthorized) &&
            evaluateVisibleKey(base)
          )
        },
      }
    })
  }

  return applyDefaults(menuItems)
}
